import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import documentStyles from "~/components/Document/documentStyles.module.scss";
import SystemProposalDisplay from "~/components/Document/SystemProposalDisplay";
import { TOAST_MESSAGES } from "~/constants/messages";
import EstimationDescription from "~/features/estimation/estimationDescription";
import EstimationTable from "~/features/estimation/estimationTable";
import { DocumentApi } from "~/functions/api/document";
import { dateToString } from "~/functions/datetime";
import { renderMarkdownToNode } from "~/hooks/useMarkdownRenderer";
import type { Document } from "~/interface/document";
import type { RootState } from "~/store";
import HistoryAppDesign from "../historyAppDesign";
import HistoryCustomizeSpecification from "../historyCustomizeSpecification";
import HistoryEntityRelationship from "../historyEntityRelationship";
import HistoryFieldDesign from "../historyFieldDesign";
import HistoryFunctionalRequirements from "../historyFunctionalRequirements";
import HistoryNonFunctionalRequirements from "../historyNonFunctionalRequirements";
import HistoryPluginProposal from "../historyPluginProposal";
import HistorySystemArchitecture from "../historySystemArchitecture";
import HistoryWorkflow from "../historyWorkflow";
import styles from "./index.module.scss";

interface IEstimationItem {
  selected: boolean;
  useCase: string;
  featureCategory: string;
  implementationMethod: string;
  workload: string;
  complexity?: string;
}

interface IEstimationDescription {
  useCase: string;
  description: {
    details: string;
    reason: string;
  };
}

const renderMarkdown = renderMarkdownToNode;

// Markdown形式かどうかを判定するヘルパー
const isMarkdownContent = (content: string | undefined): boolean => {
  if (!content) return false;
  const trimmed = content.trim();
  // JSON形式は { で始まる、それ以外はMarkdown形式と判定
  return !trimmed.startsWith("{");
};

const GenerationHistoryDetail = () => {
  const [currentData, setCurrentData] = useState<Document>(); // APIから取得したデータを格納
  const [isLoading, setIsLoading] = useState(true); // ローディング状態の管理
  const [selectedData, setSelectedData] = useState<Document | null>(null); // URLに含まれるID
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const { selectedId } = useParams<{ selectedId: string }>(); // URLからIDを取得
  const navigate = useNavigate();
  const documentApi = useMemo(() => new DocumentApi(), []);
  const segments = window.location.pathname.split("/");
  const documentType = segments[segments.length - 3];

  // Helper function to format table data for estimation
  const formatTableData = (headers: string[], rows: (string[] | Record<string, string>)[]) => {
    return rows.map((row, rowIndex) => {
      if (Array.isArray(row)) {
        return row.reduce(
          (acc, value, index) => {
            const header = headers[index];
            if (header) {
              acc[header] = value;
            }
            return acc;
          },
          { id: `row-${rowIndex}` } as Record<string, string>,
        );
      } else {
        // Already an object, just add id
        return { ...row, id: `row-${rowIndex}` };
      }
    });
  };

  const parseEstimationData = (
    content: string,
  ): { items: IEstimationItem[]; descriptions: IEstimationDescription[] } => {
    try {
      const parsedData = JSON.parse(content);
      const usecaseTable = parsedData?.usecase_table;
      console.log("usecaseTable", usecaseTable);

      if (usecaseTable && Array.isArray(usecaseTable.headers) && Array.isArray(usecaseTable.rows)) {
        const formattedUsecaseData = formatTableData(usecaseTable.headers, usecaseTable.rows);

        const estimationItems: IEstimationItem[] = [];
        const estimationDescriptions: IEstimationDescription[] = [];

        formattedUsecaseData.forEach((item) => {
          const workloadSource = item["開発コスト"];
          const workloadValue =
            typeof workloadSource === "string"
              ? workloadSource.trim()
              : String(workloadSource ?? "").trim();
          const normalizedWorkload = workloadValue || "0";

          estimationItems.push({
            selected: true,
            useCase: item["ユースケース"] || "",
            featureCategory: item["ユースケース群"] || "基本機能",
            implementationMethod: item["実装方法"] || "",
            workload: normalizedWorkload,
            complexity: normalizedWorkload,
          });

          estimationDescriptions.push({
            useCase: item["ユースケース"] || "",
            description: {
              details: item["工数詳細"] || "記載なし",
              reason: item["工数算出理由"] || "記載なし",
            },
          });
        });

        return { items: estimationItems, descriptions: estimationDescriptions };
      }
    } catch (error) {
      console.error("Failed to parse estimation content:", error);
    }
    return { items: [], descriptions: [] };
  };

  const estimationColumns: Array<ColumnDef<IEstimationItem>> = [
    {
      header: "内訳",
      accessorKey: "useCase",
    },
    {
      header: "アプリ名",
      accessorKey: "featureCategory",
    },
    {
      header: "実装方法",
      accessorKey: "implementationMethod",
    },
    {
      header: "工数",
      accessorKey: "workload",
    },
  ];
  const documentName = useMemo(() => {
    const map: Record<string, string> = {
      "system-proposal": "システム概要書",
      "plugin-proposal": "プラグイン提案書",
      "functional-requirements": "機能要件書",
      "non-functional-requirements": "非機能要件書",
      "field-design": "フィールド設計書",
      "app-design": "アプリ設計書",
      workflow: "業務フロー図",
      "entity-relationship": "ER図",
      "system-architecture": "システム構成図",
      "customize-specification": "カスタマイズ仕様書",
      estimation: "概算工数見積り",
    };
    return map[documentType!] ?? documentType;
  }, [documentType]);

  const parsedSelectedSystemProposal = useMemo(() => {
    if (documentType !== "system-proposal") return null;
    const raw = selectedData?.content as unknown as string | undefined;
    if (!raw) return null;
    // Markdown形式の場合はそのまま返す（後でisMarkdownModeで判定）
    if (isMarkdownContent(raw)) {
      return { __markdownContent: raw };
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [documentType, selectedData]);

  const parsedCurrentSystemProposal = useMemo(() => {
    if (documentType !== "system-proposal") return null;
    const raw = currentData?.content as unknown as string | undefined;
    if (!raw) return null;
    // Markdown形式の場合はそのまま返す
    if (isMarkdownContent(raw)) {
      return { __markdownContent: raw };
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [documentType, currentData]);

  // 履歴データがMarkdown形式かどうかを判定
  const isSelectedMarkdownMode = useMemo(() => {
    return parsedSelectedSystemProposal?.__markdownContent !== undefined;
  }, [parsedSelectedSystemProposal]);

  const isCurrentMarkdownMode = useMemo(() => {
    return parsedCurrentSystemProposal?.__markdownContent !== undefined;
  }, [parsedCurrentSystemProposal]);

  useEffect(() => {
    setIsLoading(true);
    const fetchComparableData = async () => {
      if (!currentProject) {
        // プロジェクトが選択されていない場合はホームページにリダイレクト
        navigate(`/${currentUser.organization_id}/home`);
        return;
      }

      if (!currentProject?.outputs?.[documentType!]?.id) {
        toast.error(TOAST_MESSAGES.HISTORY.DOCUMENT_NOT_GENERATED(documentName ?? ""));
        navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
        return;
      }
      // 現在バージョンのドキュメントを取得
      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        currentProject.outputs?.[documentType!]?.id || "",
      );
      if (response !== null) {
        setCurrentData(response); // APIから取得したデータをセット
      }

      // 選択されたバージョンのドキュメントを取得
      if (selectedId) {
        const responseDataById = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          selectedId,
        );
        if (responseDataById !== null) {
          setSelectedData(responseDataById); // APIから取得したデータをセット
        }
      }
      setIsLoading(false);
    };

    fetchComparableData();
  }, [selectedId, currentProject, currentUser, documentType, documentName, navigate, documentApi]);

  if (isLoading) {
    return <div>データをロード中です...</div>;
  }

  if (!currentData) {
    return <div>データを取得できませんでした。</div>;
  }

  const handleGoBack = () => {
    navigate(-1);
  };
  const documentTitle = selectedData?.created_at
    ? `${documentName} ${dateToString(selectedData.created_at)}`
    : `${documentName}`;

  return (
    <div className={styles["history"]} style={{ position: "relative" }}>
      <div className={styles["header"]}>
        <div className={styles["header__navi"]}>
          <p>{documentName}</p>
          <p>＞</p>
          <p>生成履歴</p>
          <p>＞</p>
          <p>生成履歴 詳細</p>
        </div>
        <div className={styles["header__title"]}>
          <ArrowBackIcon
            {...(styles["icon"] && { className: styles["icon"] })}
            onClick={handleGoBack}
          />
          <h2>{documentTitle}</h2>
        </div>
      </div>
      {documentType === "workflow" ? (
        <div>{selectedData && <HistoryWorkflow formData={selectedData} />}</div>
      ) : documentType === "entity-relationship" ? (
        <div>{selectedData && <HistoryEntityRelationship formData={selectedData} />}</div>
      ) : documentType === "system-architecture" ? (
        <div>{selectedData && <HistorySystemArchitecture formData={selectedData} />}</div>
      ) : (
        <div className={styles["body"]}>
          <div className={styles["past"]}>
            <div className={styles["pastContent"]}>
              <div className={styles["pastVer"]}>バージョン</div>
              {documentType === "system-proposal" ? (
                isSelectedMarkdownMode ? (
                  <div className={documentStyles["document-content"]}>
                    <div className={documentStyles["markdown-mode-content"]}>
                      <div className={documentStyles["markdown-content"]}>
                        {renderMarkdown(parsedSelectedSystemProposal?.__markdownContent || "")}
                      </div>
                    </div>
                  </div>
                ) : (
                  <SystemProposalDisplay
                    data={parsedSelectedSystemProposal ?? {}}
                    comparisonData={currentData}
                    isComparisonMode={true}
                  />
                )
              ) : documentType === "functional-requirements" ? (
                <HistoryFunctionalRequirements formData={selectedData ? [selectedData] : []} />
              ) : documentType === "non-functional-requirements" ? (
                <HistoryNonFunctionalRequirements formData={selectedData ? [selectedData] : []} />
              ) : documentType === "plugin-proposal" ? (
                <HistoryPluginProposal formData={selectedData ? [selectedData] : []} />
              ) : documentType === "field-design" ? (
                <HistoryFieldDesign formData={selectedData ? [selectedData] : []} />
              ) : documentType === "app-design" ? (
                <HistoryAppDesign formData={selectedData ? [selectedData] : []} />
              ) : documentType === "customize-specification" ? (
                <HistoryCustomizeSpecification formData={selectedData ? [selectedData] : []} />
              ) : documentType === "estimation" ? (
                <div>
                  {selectedData &&
                    (() => {
                      const { items, descriptions } = parseEstimationData(selectedData.content);
                      return (
                        <div>
                          <h3>概算工数見積書</h3>
                          <EstimationTable data={items} columns={estimationColumns} />
                          <h2>根拠説明</h2>
                          <EstimationDescription descriptions={descriptions} />
                        </div>
                      );
                    })()}
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles["current"]}>
            <div className={styles["currentContent"]}>
              <div className={styles["currentVer"]}>現在のバージョン</div>
              {documentName === "システム概要書" ? (
                isCurrentMarkdownMode ? (
                  <div className={documentStyles["document-content"]}>
                    <div className={documentStyles["markdown-mode-content"]}>
                      <div className={documentStyles["markdown-content"]}>
                        {renderMarkdown(parsedCurrentSystemProposal?.__markdownContent || "")}
                      </div>
                    </div>
                  </div>
                ) : (
                  <SystemProposalDisplay
                    data={parsedCurrentSystemProposal ?? {}}
                    isComparisonMode={true}
                  />
                )
              ) : documentName === "機能要件書" ? (
                <HistoryFunctionalRequirements formData={currentData ? [currentData] : []} />
              ) : documentName === "非機能要件書" ? (
                <HistoryNonFunctionalRequirements formData={currentData ? [currentData] : []} />
              ) : documentName === "プラグイン提案書" ? (
                <HistoryPluginProposal formData={currentData ? [currentData] : []} />
              ) : documentName === "フィールド設計書" ? (
                <HistoryFieldDesign formData={currentData ? [currentData] : []} />
              ) : documentName === "アプリ設計書" ? (
                <HistoryAppDesign formData={currentData ? [currentData] : []} />
              ) : documentName === "カスタマイズ仕様書" ? (
                <HistoryCustomizeSpecification formData={currentData ? [currentData] : []} />
              ) : documentName === "概算工数見積り" ? (
                <div>
                  {currentData &&
                    (() => {
                      const { items, descriptions } = parseEstimationData(currentData.content);
                      return (
                        <div>
                          <h3>概算工数見積書</h3>
                          <EstimationTable data={items} columns={estimationColumns} />
                          <h2>根拠説明</h2>
                          <EstimationDescription descriptions={descriptions} />
                        </div>
                      );
                    })()}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerationHistoryDetail;
